import React from 'react';
import { SeatPlanItem, SeatRequestItem } from '../../services/scheduledSeatService';
import { rejectEventCount, requestDisplayStatus, requestSlaState } from '../../components/ScheduledCommandPanel/utils';

type RequestLane = 'ALL' | 'OPEN' | 'OFFER' | 'PENDING' | 'CLOSED';

type Params = {
  requests: SeatRequestItem[];
  seatPlan: SeatPlanItem[];
  inventory: Array<{ fromCode?: string; toCode?: string; source?: string; status?: string }>;
  requestSearch: string;
  requestLaneFilter: RequestLane;
  inventorySearch: string;
  seatPlanSearch: string;
  seatPlanDate: string;
  calendarMonth: string;
  bookingPolicyMinHoursBeforeDeparture: number;
  setCalendarMonth: React.Dispatch<React.SetStateAction<string>>;
  setSeatPlanDate: React.Dispatch<React.SetStateAction<string>>;
  setExpandedPlanId: React.Dispatch<React.SetStateAction<string>>;
};

export const useScheduledPlanAnalytics = ({
  requests,
  seatPlan,
  inventory,
  requestSearch,
  requestLaneFilter,
  inventorySearch,
  seatPlanSearch,
  seatPlanDate,
  calendarMonth,
  bookingPolicyMinHoursBeforeDeparture,
  setCalendarMonth,
  setSeatPlanDate,
  setExpandedPlanId,
}: Params) => {
  const laneByStatus = (status?: string): 'OPEN' | 'OFFER' | 'PENDING' | 'CLOSED' => {
    const s = String(status || '').toUpperCase();
    if (['OFFER_SENT', 'ALTERNATIVE_SENT', 'MATCHED', 'AWAITING_CLIENT'].includes(s)) return 'OFFER';
    if (['PENDING_APPROVAL'].includes(s)) return 'PENDING';
    if (['CLOSED_WON', 'CLOSED_LOST', 'DECLINED', 'CONFIRMED', 'REJECTED', 'EXPIRED', 'CANCELLED'].includes(s)) return 'CLOSED';
    return 'OPEN';
  };

  const laneCounts = React.useMemo(() => {
    const counts = { OPEN: 0, OFFER: 0, PENDING: 0, CLOSED: 0 };
    requests.forEach((r) => {
      counts[laneByStatus(requestDisplayStatus(r))] += 1;
    });
    return counts;
  }, [requests]);

  const dedupedSeatPlan = React.useMemo(() => {
    const bySignature = new Map<string, SeatPlanItem>();
    seatPlan.forEach((plan) => {
      const signature = [
        plan.fromCode,
        plan.toCode,
        new Date(plan.departureAt).toISOString(),
        plan.status,
        plan.source,
        plan.totalSeats,
        plan.availableSeats,
        plan.heldSeats,
        plan.soldSeats,
        plan.seatPriceUSD,
      ].join('|');
      const existing = bySignature.get(signature);
      if (!existing) {
        bySignature.set(signature, plan);
        return;
      }
      const existingTs = new Date(existing.departureAt).getTime();
      const planTs = new Date(plan.departureAt).getTime();
      if (planTs <= existingTs) bySignature.set(signature, plan);
    });
    return Array.from(bySignature.values());
  }, [seatPlan]);

  const filteredRequests = React.useMemo(() => {
    const q = requestSearch.trim().toLowerCase();
    return requests
      .filter((r) => {
        const lane = laneByStatus(requestDisplayStatus(r));
        const laneOk = requestLaneFilter === 'ALL' ? true : lane === requestLaneFilter;
        if (!laneOk) return false;
        if (!q) return true;
        return [
          r.customerName || '',
          r.fromEmail || '',
          r.fromCode || '',
          r.toCode || '',
          requestDisplayStatus(r),
        ].join(' ').toLowerCase().includes(q);
      })
      .sort((a, b) => {
        const slaDiff = requestSlaState(b).rank - requestSlaState(a).rank;
        if (slaDiff !== 0) return slaDiff;
        return new Date(b.updatedAt || b.createdAt || 0).getTime() - new Date(a.updatedAt || a.createdAt || 0).getTime();
      });
  }, [requests, requestSearch, requestLaneFilter]);

  const filteredInventory = React.useMemo(() => {
    const q = inventorySearch.trim().toLowerCase();
    if (!q) return inventory;
    return inventory.filter((item) =>
      [item.fromCode, item.toCode, item.source, item.status]
        .join(' ')
        .toLowerCase()
        .includes(q)
    );
  }, [inventory, inventorySearch]);

  const filteredSeatPlan = React.useMemo(() => {
    const q = seatPlanSearch.trim().toLowerCase();
    return dedupedSeatPlan.filter((item) => {
      const day = new Date(item.departureAt).toISOString().slice(0, 10);
      const dayOk = seatPlanDate ? day === seatPlanDate : true;
      if (!dayOk) return false;
      if (!q) return true;
      return [
        item.fromCode,
        item.toCode,
        item.status,
        item.source,
        ...item.bookings.map((b) => `${b.customerName || ''} ${b.customerEmail || ''}`),
      ]
        .join(' ')
        .toLowerCase()
        .includes(q);
    });
  }, [dedupedSeatPlan, seatPlanSearch, seatPlanDate]);

  const calendarDays = React.useMemo(() => {
    const base = new Date(`${calendarMonth}T00:00:00.000Z`);
    if (Number.isNaN(base.getTime())) return [] as Array<{ iso: string; day: number; inMonth: boolean; slots: number; availableSeats: number; soldSeats: number }>;
    const year = base.getUTCFullYear();
    const month = base.getUTCMonth();
    const first = new Date(Date.UTC(year, month, 1));
    const startOffset = (first.getUTCDay() + 6) % 7;
    const start = new Date(Date.UTC(year, month, 1 - startOffset));
    const days: Array<{ iso: string; day: number; inMonth: boolean; slots: number; availableSeats: number; soldSeats: number }> = [];

    const byDate = new Map<string, { slots: number; availableSeats: number; soldSeats: number }>();
    dedupedSeatPlan.forEach((p) => {
      const iso = new Date(p.departureAt).toISOString().slice(0, 10);
      const row = byDate.get(iso) || { slots: 0, availableSeats: 0, soldSeats: 0 };
      row.slots += 1;
      row.availableSeats += Number(p.availableSeats || 0);
      row.soldSeats += Number(p.soldSeats || 0);
      byDate.set(iso, row);
    });

    for (let i = 0; i < 42; i += 1) {
      const d = new Date(start);
      d.setUTCDate(start.getUTCDate() + i);
      const iso = d.toISOString().slice(0, 10);
      const stats = byDate.get(iso) || { slots: 0, availableSeats: 0, soldSeats: 0 };
      days.push({
        iso,
        day: d.getUTCDate(),
        inMonth: d.getUTCMonth() === month,
        slots: stats.slots,
        availableSeats: stats.availableSeats,
        soldSeats: stats.soldSeats,
      });
    }
    return days;
  }, [calendarMonth, dedupedSeatPlan]);

  const monthSummary = React.useMemo(() => {
    const base = new Date(`${calendarMonth}T00:00:00.000Z`);
    if (Number.isNaN(base.getTime())) {
      return { flights: 0, availableSeats: 0, soldSeats: 0, avgLoadPct: 0 };
    }
    const y = base.getUTCFullYear();
    const m = base.getUTCMonth();
    const inMonth = dedupedSeatPlan.filter((plan) => {
      const d = new Date(plan.departureAt);
      return d.getUTCFullYear() === y && d.getUTCMonth() === m;
    });
    const flights = inMonth.length;
    const availableSeats = inMonth.reduce((sum, row) => sum + Number(row.availableSeats || 0), 0);
    const soldSeats = inMonth.reduce((sum, row) => sum + Number(row.soldSeats || 0), 0);
    const totalSeats = inMonth.reduce((sum, row) => sum + Number(row.totalSeats || 0), 0);
    const avgLoadPct = totalSeats > 0 ? Math.round((soldSeats / totalSeats) * 100) : 0;
    return { flights, availableSeats, soldSeats, avgLoadPct };
  }, [calendarMonth, dedupedSeatPlan]);

  const operationsDayPlans = React.useMemo(() => {
    if (!seatPlanDate) return [] as SeatPlanItem[];
    return dedupedSeatPlan
      .filter((plan) => new Date(plan.departureAt).toISOString().slice(0, 10) === seatPlanDate)
      .sort((a, b) => new Date(a.departureAt).getTime() - new Date(b.departureAt).getTime());
  }, [seatPlanDate, dedupedSeatPlan]);

  const selectedDaySummary = React.useMemo(() => {
    if (!operationsDayPlans.length) {
      return {
        flights: 0,
        soldSeats: 0,
        availableSeats: 0,
        totalSeats: 0,
        loadPct: 0,
        openForBooking: 0,
        rejectedRequests: 0,
      };
    }
    const now = Date.now();
    const minHours = Math.max(0, Number(bookingPolicyMinHoursBeforeDeparture || 0));
    const planIds = new Set(operationsDayPlans.map((p) => p.id));
    const soldSeats = operationsDayPlans.reduce((sum, p) => sum + Number(p.soldSeats || 0), 0);
    const availableSeats = operationsDayPlans.reduce((sum, p) => sum + Number(p.availableSeats || 0), 0);
    const totalSeats = operationsDayPlans.reduce((sum, p) => sum + Number(p.totalSeats || 0), 0);
    const openForBooking = operationsDayPlans.reduce((sum, p) => {
      const depMs = new Date(p.departureAt).getTime();
      const cutoffMs = depMs - minHours * 60 * 60 * 1000;
      return sum + (cutoffMs > now ? 1 : 0);
    }, 0);
    const rejectedRequests = requests.reduce((sum, r) => {
      if (!planIds.has(String(r.matchedInventory?.id || ''))) return sum;
      return sum + rejectEventCount(r as any);
    }, 0);
    const loadPct = totalSeats > 0 ? Math.round((soldSeats / totalSeats) * 100) : 0;
    return {
      flights: operationsDayPlans.length,
      soldSeats,
      availableSeats,
      totalSeats,
      loadPct,
      openForBooking,
      rejectedRequests,
    };
  }, [operationsDayPlans, bookingPolicyMinHoursBeforeDeparture, requests]);

  const monthTitle = React.useMemo(() => {
    const d = new Date(`${calendarMonth}T00:00:00.000Z`);
    return d.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
  }, [calendarMonth]);

  const shiftCalendarMonth = (diff: number) => {
    const base = new Date(`${calendarMonth}T00:00:00.000Z`);
    if (Number.isNaN(base.getTime())) return;
    const next = new Date(Date.UTC(base.getUTCFullYear(), base.getUTCMonth() + diff, 1));
    setCalendarMonth(next.toISOString().slice(0, 10));
  };

  const selectCalendarDate = (isoDate: string) => {
    setSeatPlanDate(isoDate);
    setExpandedPlanId('');
  };

  React.useEffect(() => {
    if (!seatPlanDate || !operationsDayPlans.length) {
      setExpandedPlanId('');
      return;
    }
    setExpandedPlanId((prev) => (operationsDayPlans.some((p) => p.id === prev) ? prev : operationsDayPlans[0].id));
  }, [seatPlanDate, operationsDayPlans, setExpandedPlanId]);

  return {
    calendarDays,
    dedupedSeatPlan,
    filteredInventory,
    filteredRequests,
    filteredSeatPlan,
    laneByStatus,
    laneCounts,
    monthSummary,
    monthTitle,
    operationsDayPlans,
    selectCalendarDate,
    selectedDaySummary,
    shiftCalendarMonth,
  };
};
